import React, { useMemo } from 'react';
import { PageContentWrapper } from '../../shared/styled/PageContentStyle';
import Title from './Title';
import { FilesTableColumns } from '../../configs/files';
import { api } from '../../shared/hooks/api';
import GroupedFileTable from './Content/GroupedFileTable';
import PlainFileTable from './Content/PlainFileTable';
import SearchFilterBar from '../../shared/components/SearchFilterBar';
import PaginationBar from '../../shared/components/PaginationBar';
import { ContentWrapper, ExpandableContent } from '../../shared/styled/TableStyles';
import Spinner from '../../shared/components/LoadingPage';
import { DEFAULT_PAGE_SIZE } from '../../configs/apiConfig';
import { useTabURLSync } from '../../shared/hooks/useTabURLSync';
import { useLocation, useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import { ToggleButton, ToggleButtonGroup, Box } from '@mui/material';
import { List, FolderTree } from 'lucide-react';
import { FileFilterType } from '../../configs/filterTypes';

const FileList = () => {
    const { updateCurrentTabParams } = useTabURLSync();
    const location = useLocation();
    const navigate = useNavigate();
    const expandableContentRef = useRef<HTMLDivElement>(null);

    const searchParams = useMemo(() => {
        return new URLSearchParams(window.location.search);
    }, []);

    const searchValue = searchParams.get("search");
    const pageFromURL = searchParams.get("page");
    const pageSizeFromURL = searchParams.get("page_size");
    const createdAtGte = searchParams.get("created_at__gte");
    const createdAtLte = searchParams.get("created_at__lte");

    const initialParams = {
        page_size: pageSizeFromURL ? parseInt(pageSizeFromURL) : DEFAULT_PAGE_SIZE,
        page: pageFromURL ? parseInt(pageFromURL) : 1,
        ...(searchValue && searchValue.trim() !== "" && { search: searchValue }),
        ...(createdAtGte && { created_at__gte: createdAtGte }),
        ...(createdAtLte && { created_at__lte: createdAtLte }),
    };

    const [{ data, variables, isLoading }, fetchItems] = api.get(
        '/files/',
        initialParams,
        false,
    );

    const [{ }, refreshFetchItems] = api.get('/files/', {}, false);

    const [currentPage, setCurrentPage] = useState(initialParams.page);
    const [viewMode, setViewMode] = useState<'grouped' | 'list'>('list');

    const rawItems = data?.results || [];
    const totalItems = data?.count || 0;
    const pageSize = variables?.page_size || initialParams.page_size;

    const syncURLWithState = useCallback(() => {
        const currentState = {
            page: currentPage.toString(),
            page_size: pageSize.toString(),
            ...(searchValue && { search: searchValue }),
        };
        updateCurrentTabParams(currentState);
    }, [currentPage, pageSize, searchValue, updateCurrentTabParams]);

    useEffect(() => {
        if (!isLoading && data) {
            const timer = setTimeout(() => {
                syncURLWithState();
            }, 100);
            return () => clearTimeout(timer);
        }
    }, [data, currentPage, pageSize, searchValue, isLoading, syncURLWithState]);

    useEffect(() => {
        if (pageFromURL) {
            setCurrentPage(parseInt(pageFromURL));
        }
    }, [pageFromURL]);

    const clearAllFilters = () => {
        setCurrentPage(1);
        const currentTab = new URLSearchParams(location.search).get("tab");
        if (currentTab) {
            navigate(`${location.pathname}?tab=${currentTab}`);
        } else {
            navigate(location.pathname);
        }
        // Don't call fetchItems here - let the FilterOptions useEffect or URL change handle it
    };

    const refreshData = () => {
        fetchItems();
    };

    return (
        <PageContentWrapper>
            <Title onRefreshList={refreshFetchItems} />
            <ContentWrapper>
                <ExpandableContent ref={expandableContentRef}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                        <SearchFilterBar
                            textString='Tìm kiếm theo từ khóa Tên tập tin, Mã đơn hàng, Tên khách hàng'
                            fetchItems={fetchItems}
                            filterTypes={FileFilterType}
                            setCurrentPage={setCurrentPage}
                            onRefresh={refreshData}
                            onClearFilters={clearAllFilters}
                        />
                        <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                            <ToggleButtonGroup
                                value={viewMode}
                                exclusive
                                onChange={(e, newMode) => {
                                    if (newMode !== null) {
                                        setViewMode(newMode);
                                    }
                                }}
                                aria-label="view mode"
                                size="small"
                            >
                                <ToggleButton value="grouped" aria-label="grouped view">
                                    <FolderTree size={18} />
                                </ToggleButton>
                                <ToggleButton value="list" aria-label="list view">
                                    <List size={18} />
                                </ToggleButton>
                            </ToggleButtonGroup>
                        </Box>
                    </Box>
                    {isLoading ? (
                        <Spinner />
                    ) : (
                        <>
                            {viewMode === 'grouped' ? (
                                <GroupedFileTable
                                    itemList={rawItems}
                                    extraRowProps={{ fetchItems }}
                                />
                            ) : (
                                <PlainFileTable
                                    itemList={rawItems}
                                    extraRowProps={{ fetchItems }}
                                />
                            )}
                            {rawItems.length <= 15 && (
                                <PaginationBar
                                    totalItems={totalItems}
                                    pageSize={pageSize}
                                    fetchItems={fetchItems}
                                    currentPage={currentPage}
                                    setCurrentPage={setCurrentPage}
                                />
                            )}
                        </>
                    )}
                </ExpandableContent>
                {rawItems.length > 15 && (
                    <PaginationBar
                        totalItems={totalItems}
                        pageSize={pageSize}
                        fetchItems={fetchItems}
                        currentPage={currentPage}
                        setCurrentPage={setCurrentPage}
                    />
                )}
            </ContentWrapper>
        </PageContentWrapper>
    );
};

export default FileList;
